import { Box, Chip, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { healthCheck } from "../../utils/companion";

/**
 * Displays whether the desktop companion app is reachable.
 *
 * Props:
 * - url?: override websocket URL
 * - timeoutMs?: connection timeout for each check
 * - pollIntervalMs?: if > 0, re-check periodically
 * - label?: text shown before the status chip
 */
export const CompanionHealthCheck = ({
	url,
	timeoutMs = 750,
	pollIntervalMs = 0,
	label = "Desktop App",
}) => {
	const { t } = useTranslation();
	const [status, setStatus] = useState("down"); // 'up' | 'down'
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const runCheck = useCallback(async () => {
		const running = await healthCheck({ url, timeoutMs }).catch(() => false);
		if (!mountedRef.current) return;
		setStatus(running ? "up" : "down");
	}, [url, timeoutMs]);

	useEffect(() => {
		void runCheck();
	}, [runCheck]);

	useEffect(() => {
		if (!pollIntervalMs || pollIntervalMs <= 0) return;
		const interval = setInterval(() => {
			void runCheck();
		}, pollIntervalMs);
		return () => clearInterval(interval);
	}, [pollIntervalMs, runCheck]);

	let chipProps;
	switch (status) {
		case "up":
			chipProps = { label: t("connected"), color: "success" };
			break;
		case "down":
			chipProps = { label: t("not_connected"), color: "error" };
			break;
	}

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
			<Typography variant="body2">{label}:</Typography>
			<Chip size="small" variant="outlined" {...chipProps} />
		</Box>
	);
};

CompanionHealthCheck.propTypes = {
	url: PropTypes.string,
	timeoutMs: PropTypes.number,
	pollIntervalMs: PropTypes.number,
	label: PropTypes.string,
};
